<template>
  <Transition name="modal-fade">
    <div class="modal-overlay" v-if="show" @click.self="closeModal">
      <!-- Back to map button positioned outside the modal (list view) -->
      <button v-if="view === 'list'" class="back-button back-to-map outside-modal" @click="backToMap">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M19 12H5M12 19l-7-7 7-7"></path>
        </svg>
        BACK TO MAP
      </button>
      
      <!-- Back to list button positioned outside the modal (detail view) -->
      <button v-if="view === 'detail'" class="back-button back-to-list outside-modal" @click="backToList">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M19 12H5M12 19l-7-7 7-7"></path>
        </svg>
        BACK TO LIST
      </button>
      
      <div class="modal-content">
        <button class="close-button" @click="closeModal">&times;</button>
        
        <Transition name="view-fade" mode="out-in">
          <!-- People List View -->
          <div v-if="view === 'list'" key="list-view" class="list-container">
            
            <h2>{{ region.name }}</h2>
            <div class="people-grid">
              <div 
                v-for="(person, index) in people" 
                :key="person.id" 
                class="person-card"
                :style="{ animationDelay: `${index * 0.05}s` }"
                :class="{ 'animate-in': animateList }"
                @click="showPerson(person)"
              >
                <img :src="person.thumbnail || '/thumbnails/placeholder.png'" 
                  :alt="person.name" 
                  class="thumbnail">
                <div class="person-info">
                  <h3>{{ person.name }}</h3>
                  <p class="location">{{ person.location || 'No location' }}</p>
                  <!-- <span class="content-badge" :class="person.contentType">
                    {{ person.contentType }}
                  </span> -->
                </div>
              </div>
            </div>
          </div>
          
          <!-- Person Detail View -->
          <div v-else-if="view === 'detail'" key="detail-view" class="person-detail">
            <div class="detail-container">
              <!-- Header information for video type only -->
              <template v-if="selectedPerson.contentType === 'video'">
                <h2 class="detail-name">{{ selectedPerson.name }}</h2>
                <p class="detail-location">{{ selectedPerson.location || 'No location' }}</p>
                <p class="detail-years" v-if="selectedPerson.years">Years at Pratt & Whitney: {{ selectedPerson.years }}</p>
              </template>
              
              <!-- Content section -->
              <Transition name="fade" mode="out-in">
                <!-- Video Content -->
                <div v-if="selectedPerson.contentType === 'video'" key="video" class="video-container">
                  <video 
                    ref="videoPlayer" 
                    class="video-player" 
                    :src="localizedVideoUrl"
                    controls
                    autoplay
                    preload="metadata"
                    @timeupdate="updateProgress"
                    @ended="handleVideoEnd"
                  >
                    <track 
                      kind="subtitles" 
                      :src="selectedPerson.captionsUrl" 
                      label="English" 
                      srclang="en"
                      @load="handleCaptionsLoaded"
                      ref="captionTrack"
                      default
                    >
                    Your browser does not support the video tag.
                  </video>
                  
                  <!-- Video captions -->
                  <!-- <div class="video-caption" v-if="captionText">
                    {{ captionText }}
                  </div> -->
                  
                  <!-- Custom video controls -->
                  <div class="custom-video-controls">
                    <button @click="skipBackward" class="control-button skip-backward">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M11 17l-5-5 5-5M18 17l-5-5 5-5" />
                      </svg>
                    </button>
                    
                    <button @click="togglePlay" class="control-button play-pause">
                      <svg v-if="!isPlaying" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polygon points="5 3 19 12 5 21 5 3" />
                      </svg>
                      <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="6" y="4" width="4" height="16" />
                        <rect x="14" y="4" width="4" height="16" />
                      </svg>
                    </button>
                    
                    <button @click="skipForward" class="control-button skip-forward">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M13 17l5-5-5-5M6 17l5-5-5-5" />
                      </svg>
                    </button>
                  </div>
                  
                  <!-- Progress bar with current time and duration -->
                  <!-- <div class="video-progress-container">
                    <span class="current-time">{{ formatTime(currentTime) }}</span>
                    <div class="progress-bar-container">
                      <div class="progress-bar" :style="{ width: `${progressPercentage}%` }"></div>
                    </div>
                    <span class="duration">{{ formatTime(duration) }}</span>
                  </div> -->
                </div>
                
                <!-- Message Content -->
                <div v-else-if="selectedPerson.contentType === 'message'" key="message" class="message-container">
                  <div class="message-layout">
                    <div class="message-photo-column">
                      <img 
                        :src="selectedPerson.thumbnail || '/thumbnails/placeholder.png'" 
                        :alt="selectedPerson.name" 
                        class="detail-thumbnail">
                    </div>
                    <div class="message-content-column">
                      <h3 class="detail-name">{{ selectedPerson.name }}</h3>
                      <p class="detail-location">{{ selectedPerson.location || 'No location' }}</p>
                      <p class="detail-years" v-if="selectedPerson.years">Years at Pratt & Whitney: {{ selectedPerson.years }}</p>
                      <p class="message-text">"{{ selectedPerson.message }}"</p>
                    </div>
                  </div>
                </div>
              </Transition>
              
              <!-- Person description for video type only -->
              <!-- <p v-if="selectedPerson.contentType === 'video'" class="detail-description">{{ selectedPerson.description }}</p> -->
            </div>
          </div>
        </Transition>
      </div>
    </div>
  </Transition>
</template>

<script>
import VideoPlayer from './VideoPlayer.vue'
import MessageDisplay from './MessageDisplay.vue'
import dataService from '../services/dataService'

export default {
  name: 'GalleryModal',
  components: {
    VideoPlayer,
    MessageDisplay
  },
  props: {
    show: Boolean,
    region: Object,
    currentLanguage: {
      type: String,
      default: 'en'
    }
  },
  data() {
    return {
      view: 'list',
      people: [],
      selectedPerson: null,
      animateList: false,
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      progressPercentage: 0,
      captionText: '',
      activeCue: null
    }
  },
  computed: {
    localizedVideoUrl() {
      if (!this.selectedPerson || this.selectedPerson.contentType !== 'video') {
        return null
      }
      
      if (this.currentLanguage === 'fr') {
        // Replace the file extension with _french + extension
        const url = this.selectedPerson.videoUrl
        const lastDotIndex = url.lastIndexOf('.')
        if (lastDotIndex !== -1) {
          const baseName = url.substring(0, lastDotIndex)
          const extension = url.substring(lastDotIndex)
          return `${baseName}_french${extension}`
        }
      }
      
      return this.selectedPerson.videoUrl
    }
  },
  methods: {
    closeModal() {
      this.view = 'list'
      this.selectedPerson = null
      this.$emit('close')
    },
    showPerson(person) {
      this.selectedPerson = person
      this.view = 'detail'
      
      // Reset video state when showing a new person
      this.isPlaying = false
      this.currentTime = 0
      this.duration = 0
      this.progressPercentage = 0
      this.captionText = ''
      this.activeCue = null
      
      // If it's a video, we'll need to set up the video element after rendering
      if (person.contentType === 'video') {
        this.$nextTick(() => {
          if (this.$refs.videoPlayer) {
            const video = this.$refs.videoPlayer
            video.addEventListener('loadedmetadata', () => {
              this.duration = video.duration
            })
          }
        })
      }
    },
    backToList() {
      // Stop video if playing
      if (this.selectedPerson?.contentType === 'video' && this.$refs.videoPlayer) {
        this.$refs.videoPlayer.pause()
      }
      
      this.view = 'list'
      this.animateList = true
    },
    backToMap() {
      // Immediately close the modal and emit the backToMap event
      this.$emit('backToMap')
      this.$emit('close')
      
      // Reset component state
      this.view = 'list'
      this.selectedPerson = null
    },
    togglePlay() {
      if (this.$refs.videoPlayer) {
        const video = this.$refs.videoPlayer
        if (video.paused) {
          video.play()
          this.isPlaying = true
        } else {
          video.pause()
          this.isPlaying = false
        }
      }
    },
    skipForward() {
      if (this.$refs.videoPlayer) {
        const video = this.$refs.videoPlayer
        video.currentTime = Math.min(video.currentTime + 10, video.duration)
      }
    },
    skipBackward() {
      if (this.$refs.videoPlayer) {
        const video = this.$refs.videoPlayer
        video.currentTime = Math.max(video.currentTime - 10, 0)
      }
    },
    updateProgress() {
      if (this.$refs.videoPlayer) {
        const video = this.$refs.videoPlayer
        this.currentTime = video.currentTime
        this.progressPercentage = (video.currentTime / video.duration) * 100
        this.isPlaying = !video.paused
      }
    },
    formatTime(seconds) {
      const mins = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
    },
    handleCaptionsLoaded() {
      if (this.$refs.captionTrack && this.$refs.videoPlayer) {
        const track = this.$refs.captionTrack.track;
        
        // Listen for cue changes
        track.addEventListener('cuechange', () => {
          // Update the active cue
          this.activeCue = track.activeCues.length > 0 ? track.activeCues[0] : null;
          this.captionText = this.activeCue ? this.activeCue.text : '';
        });
      }
    },
    handleVideoEnd() {
      // Automatically go back to list view when video ends
      this.backToList();
    }
  },
  watch: {
    currentLanguage(newLanguage, oldLanguage) {
      // If we're currently viewing a video, reload it with the new language
      if (this.view === 'detail' && this.selectedPerson?.contentType === 'video') {
        this.$nextTick(() => {
          if (this.$refs.videoPlayer) {
            const video = this.$refs.videoPlayer
            const currentTime = video.currentTime
            const wasPlaying = !video.paused
            
            // Load new video source
            video.src = this.localizedVideoUrl
            video.load()
            
            // Restore playback state after metadata loads
            video.addEventListener('loadedmetadata', () => {
              video.currentTime = currentTime
              if (wasPlaying) {
                video.play()
              }
            }, { once: true })
          }
        })
      }
    },
    region(newRegion) {
      if (newRegion) {
        this.view = 'list'
        this.selectedPerson = null
        this.animateList = false
        
        dataService.getPeopleByRegion(newRegion.id).then(people => {
          this.people = people
          this.$nextTick(() => {
            this.animateList = true
          })
        })
      }
    },
    view(newView) {
      if (newView === 'list') {
        this.$nextTick(() => {
          this.animateList = true
        })
      }
    }
  }
}
</script>

<style scoped>
/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  padding: 0;
  margin-top: 40px; /* Add space at the top for the back to map button */
}

.close-button {
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 1.5rem;
  background: none;
  border: none;
  cursor: pointer;
  transition: transform 0.2s ease, opacity 0.2s ease;
  z-index: 10;
}

.close-button:hover {
  transform: scale(1.2);
  opacity: 0.8;
}

/* List container styles */
.list-container {
  width: 100%;
  padding: 2rem;
  position: relative;
}

.list-container h2 {
  font-size: 1.75rem;
  margin-bottom: 1.5rem;
  color: #333;
  text-align: center;
}

/* Grid and cards */
.people-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;
}

/* Card styles - Updated to match mockup */
.person-card {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  opacity: 0;
  transform: translateY(20px);
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 1rem;
  border: 1px solid #eaeaea;
}

.person-info {
  display: flex;
  flex-direction: column;
  margin-left: 1rem;
  flex: 1;
}

.person-card h3 {
  margin: 0 0 0.25rem 0;
  font-size: 1.1rem;
  color: #333;
}

.location {
  margin: 0 0 0.5rem 0;
  font-size: 0.9rem;
  color: #666;
}

/* Animation */
.person-card.animate-in {
  animation-name: card-appear;
  animation-duration: 0.3s;
  animation-fill-mode: forwards;
  animation-timing-function: ease;
}

@keyframes card-appear {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Hover effects */
.person-card.animate-in {
  transition: all 0.3s ease;
}

.person-card.animate-in:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15);
}

/* Thumbnail styles - Updated to match mockup */
.thumbnail {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #f5f5f5;
}

.person-card.animate-in .thumbnail {
  transition: transform 0.3s ease;
}

.person-card.animate-in:hover .thumbnail {
  transform: scale(1.05);
}

.content-badge {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  align-self: flex-start;
}

.content-badge.video {
  background: var(--primary-color, #4a90e2);
  color: white;
}

.content-badge.message {
  background: var(--secondary-color, #50b87a);
  color: white;
}

/* Detail view - Updated to match mockup */
.person-detail {
  width: 100%;
  position: relative;
}

.detail-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 3.5rem 2.5rem 2.5rem;
}

.detail-name {
  font-size: 2rem;
  margin: 0 0 0.5rem;
  color: #1f2937;
  font-weight: 600;
}

.detail-location {
  font-size: 1.1rem;
  margin: 0 0 0.25rem;
  color: #4b5563;
}

.detail-years {
  font-size: 1rem;
  margin: 0 0 2rem;
  color: #4b5563;
}

.detail-description {
  max-width: 800px;
  margin: 2rem auto 2rem;
  line-height: 1.6;
  color: #4b5563;
}

/* Video container styles */
.video-container {
  width: 100%;
  max-width: 700px;
  margin: 1.5rem auto;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
}

.video-player {
  width: 100%;
  border-radius: 8px;
  display: block;
}

.video-caption {
  position: absolute;
  bottom: 60px;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 0.5rem 1rem;
  text-align: center;
  z-index: 2;
}

/* Custom video controls */
.custom-video-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 1rem;
  gap: 1rem;
}

.control-button {
  background: #1f2937;
  border: none;
  color: white;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.2s;
}

.control-button svg {
  width: 24px;
  height: 24px;
}

.control-button:hover {
  background: #374151;
  transform: scale(1.1);
}

.play-pause {
  width: 60px;
  height: 60px;
}

.play-pause svg {
  width: 30px;
  height: 30px;
}

/* Video progress bar */
.video-progress-container {
  display: flex;
  align-items: center;
  margin-top: 0.75rem;
  width: 100%;
  gap: 0.75rem;
}

.current-time, .duration {
  font-size: 0.875rem;
  color: #4b5563;
  font-family: monospace;
}

.progress-bar-container {
  height: 5px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
  flex-grow: 1;
}

.progress-bar {
  height: 100%;
  background: #1f2937;
  border-radius: 3px;
  width: 0;
  transition: width 0.1s linear;
}

/* Message container styles */
.message-container {
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
}

.message-layout {
  display: flex;
  align-items: flex-start;
  gap: 2.5rem;
}

.message-photo-column {
  flex: 0 0 auto;
}

.message-content-column {
  flex: 1;
  text-align: left;
  padding-top: 0.5rem;
}

.detail-thumbnail {
  width: 260px;
  height: 260px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #f5f5f5;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.message-content-column .detail-name {
  font-size: 2.2rem;
  margin: 0 0 0.5rem;
  color: #1f2937;
  text-align: left;
}

.message-content-column .detail-location {
  font-size: 1.2rem;
  margin: 0 0 0.25rem;
  color: #4b5563;
  text-align: left;
}

.message-content-column .detail-years {
  font-size: 1.1rem;
  margin: 0 0 1.5rem;
  color: #4b5563;
  text-align: left;
}

.message-text {
  font-size: 1.1rem;
  line-height: 1.6;
  color: #1f2937;
  margin: 0;
  text-align: left;
  display: block;
}

/* Back button styles */
.back-button {
  background: none;
  color: #1f2937;
  font-size: 0.9rem;
  padding: 0.6rem 1.2rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  z-index: 5;
  border: 1px solid #1f2937;
}

.back-button svg {
  width: 18px;
  height: 18px;
}

.back-button:hover {
  background: #f5f5f5;
}

/* Back to Map button specific styles */
.back-to-map {
  color: white;
  border-color: white;
}

.back-to-map:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Back to List button specific styles */
.back-to-list {
  color: white;
  border-color: white;
}

.back-to-list:hover {
  background: rgba(255, 255, 255, 0.2);
}

.outside-modal {
  position: fixed;
  top: 40px;
  left: 40px;
  z-index: 1001;
}

/* Transitions */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.view-fade-enter-active,
.view-fade-leave-active {
  transition: opacity 0.2s ease;
}

.view-fade-enter-from,
.view-fade-leave-to {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>